import React, { useEffect, useState } from 'react';
import { RobuxIcon, useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { Trophy, Timer, Coins, Medal, Swords, Crown, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

// Chat bileşenini import ediyoruz
import { useChatLayout } from '../context/ChatLayoutContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const formatCompact = (number) => {
  if (!number) return "0";
  return new Intl.NumberFormat('en-US', { notation: "compact", maximumFractionDigits: 2 }).format(number);
};

const formatTime = (totalSeconds) => {
  const h = Math.floor(totalSeconds / 3600).toString().padStart(2, '0');
  const m = Math.floor((totalSeconds % 3600) / 60).toString().padStart(2, '0');
  const s = (totalSeconds % 60).toString().padStart(2, '0');
  return `${h}:${m}:${s}`;
};

// ===============================================
// PAYLAŞILAN UI PARÇALARI
// ===============================================

// Oyuncu avatarı (Roblox avatarı ya da baş harf)
const PlayerAvatar = ({ username, hasMc, sizeClass, ringClass = "border-[#2A2D31]" }) =>
  hasMc ? (
    <img
      src={`${BACKEND_URL}/api/user/avatar/${username}`}
      alt="avatar"
      className={`${sizeClass} rounded-lg border ${ringClass}`}
      onError={(e) => { e.target.onerror = null; e.target.src = "/default-avatar.png"; }}
    />
  ) : (
    <div className={`${sizeClass} rounded-lg bg-gradient-to-br from-[#B059E8] to-[#7b2fc4] flex items-center justify-center font-bold text-white`}>
      {username.charAt(0).toUpperCase()}
    </div>
  );

// ===============================================
// KÜRSÜ (PODIUM) AYARLARI
// 1 -> altın / orta-üst, 2 -> gümüş / sol-orta, 3 -> bronz / sağ-alt
// ===============================================
const PODIUM_CONFIG = {
  1: {
    icon: Crown,
    iconSize: 'w-7 h-7 md:w-9 md:h-9',
    avatarSize: 'w-16 h-16 md:w-20 md:h-20',
    colWidth: 'w-[40%] md:w-[34%]',
    pedestalH: 'h-28 md:h-40',
    gradient: 'from-yellow-300 via-yellow-400 to-amber-500',
    text: 'text-yellow-400',
    border: 'border-yellow-500/60',
    ring: 'border-yellow-400/70',
    glow: 'shadow-[0_0_45px_rgba(234,179,8,0.30)]',
    delay: 0.05,
  },
  2: {
    icon: Medal,
    iconSize: 'w-6 h-6 md:w-7 md:h-7',
    avatarSize: 'w-12 h-12 md:w-16 md:h-16',
    colWidth: 'w-[30%] md:w-[33%]',
    pedestalH: 'h-20 md:h-28',
    gradient: 'from-slate-200 via-slate-300 to-slate-400',
    text: 'text-slate-300',
    border: 'border-slate-400/50',
    ring: 'border-slate-300/60',
    glow: 'shadow-[0_0_30px_rgba(203,213,225,0.15)]',
    delay: 0.18,
  },
  3: {
    icon: Medal,
    iconSize: 'w-6 h-6 md:w-7 md:h-7',
    avatarSize: 'w-12 h-12 md:w-16 md:h-16',
    colWidth: 'w-[30%] md:w-[33%]',
    pedestalH: 'h-12 md:h-20',
    gradient: 'from-orange-400 via-orange-500 to-amber-700',
    text: 'text-orange-400',
    border: 'border-orange-500/50',
    ring: 'border-orange-400/60',
    glow: 'shadow-[0_0_30px_rgba(249,115,22,0.15)]',
    delay: 0.3,
  },
};

const PodiumCard = ({ player, place }) => {
  const cfg = PODIUM_CONFIG[place];

  // Oyuncu yoksa kürsü şeklini koruyan boş bir sütun bırak
  if (!player) {
    return (
      <div className={`flex flex-col items-center ${cfg.colWidth} opacity-40`}>
        <div className={`${cfg.iconSize} mb-2`} />
        <div className="w-full flex flex-col items-center gap-2 p-4 rounded-2xl border border-dashed border-[#2A2D31] bg-[#15171a]">
          <div className={`${cfg.avatarSize} rounded-lg bg-[#1f2226] border border-[#2A2D31]`} />
          <span className="text-xs text-zinc-600 font-bold">Empty</span>
        </div>
        <div className={`w-full ${cfg.pedestalH} mt-2 rounded-t-xl bg-[#1f2226] flex items-start justify-center pt-2`}>
          <span className="font-heading font-black text-3xl md:text-5xl text-white/10">{place}</span>
        </div>
      </div>
    );
  }

  const Icon = cfg.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: cfg.delay, type: 'spring', stiffness: 90 }}
      className={`flex flex-col items-center min-w-0 ${cfg.colWidth}`}
    >
      {/* Süzülen kupa / madalya */}
      <motion.div
        animate={{ y: [0, -6, 0] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut', delay: place * 0.2 }}
        className={`mb-2 ${cfg.text} drop-shadow-[0_0_10px_currentColor]`}
      >
        <Icon className={cfg.iconSize} />
      </motion.div>

      {/* Oyuncu kartı */}
      <div className={`w-full flex flex-col items-center gap-1.5 md:gap-2 p-3 md:p-4 rounded-2xl border ${cfg.border} bg-[#1A1D20] ${cfg.glow}`}>
        <div className="relative">
          <div className={`absolute -inset-1 rounded-xl bg-gradient-to-br ${cfg.gradient} opacity-40 blur-[6px]`} />
          <div className="relative">
            <PlayerAvatar
              username={player.username}
              hasMc={player.roblox_username}
              sizeClass={cfg.avatarSize}
              ringClass={`${cfg.ring} border-2`}
            />
          </div>
        </div>

        <span className="font-bold text-white text-sm md:text-base truncate max-w-full px-1 text-center">
          {player.username}
        </span>

        {/* Bahis */}
        <span className="mm-num text-[11px] md:text-xs text-[#2ecf6e]">
          {formatCompact(player.wager)}<RobuxIcon size={13} className="ml-1" /> wager
        </span>

        {/* Ödül */}
        <div className="flex items-center gap-1.5">
          <Coins className={`w-4 h-4 ${cfg.text}`} />
          <span className={`mm-num font-black text-base md:text-xl text-transparent bg-clip-text bg-gradient-to-r ${cfg.gradient}`}>
            {formatCompact(player.prize)}<RobuxIcon size={13} className="ml-1" />
          </span>
        </div>
      </div>

      {/* Kürsü basamağı (yükseklik farkı = merdiven görünümü) */}
      <div className={`w-full ${cfg.pedestalH} mt-2 rounded-t-xl bg-gradient-to-b ${cfg.gradient} flex items-start justify-center pt-1.5 md:pt-2 relative overflow-hidden`}>
        <span className="font-heading font-black text-3xl md:text-5xl text-black/25 leading-none">{place}</span>
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/25 to-transparent" />
      </div>
    </motion.div>
  );
};

// ===============================================
// SAYFA
// ===============================================
export const DailyRace = () => {
  const { token } = useAuth();
  const [raceData, setRaceData] = useState(null);
  // 💬 Aç/kapa durumu paylaşımlı: sayfa değişse de korunur.
  const { chatOpen } = useChatLayout();
  const [timeLeft, setTimeLeft] = useState(0);
  const [loading, setLoading] = useState(true);

  // Giriş yapmayanlar (Misafirler) da listeyi çekebilir
  useEffect(() => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    axios.get(`${BACKEND_URL}/api/game/daily-race`, { headers })
    .then(res => {
      setRaceData(res.data);
      setTimeLeft(res.data.time_remaining);
      setLoading(false);
    })
    .catch(() => {
      toast.error("Failed to load race data!");
      setLoading(false);
    });
  }, [token]);

  useEffect(() => {
    if (timeLeft <= 0) return;
    const timer = setInterval(() => setTimeLeft(prev => prev - 1), 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0E0F11]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#B059E8]"></div>
      </div>
    );
  }

  // İlk 3'ü kürsü için, geri kalanı liste için ayır
  const players = raceData?.top_players || [];
  const first = players.find(p => p.rank === 1) || players[0] || null;
  const second = players.find(p => p.rank === 2) || players[1] || null;
  const third = players.find(p => p.rank === 3) || players[2] || null;
  const rest = players.filter(p => ![first?.user_id, second?.user_id, third?.user_id].includes(p.user_id));

  return (
    <div className={`min-h-screen bg-[#0E0F11] pt-24 pb-12 px-4 relative overflow-hidden flex flex-col lg:flex-row gap-6 transition-[padding] duration-300 ${chatOpen ? 'md:pl-[340px] lg:pl-[370px]' : ''}`}>

      {/* Arka Plan Işık Efekti */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-amber-500/10 rounded-full blur-[120px] pointer-events-none" />

      {/* ========================================= */}
      {/* SOL SÜTUN: CANLI SOHBET (LIVE CHAT) */}
          {/* 💬 Sohbet App.js'te TEK kopya render ediliyor (ChatDock).
              Sayfa-içi kopya kaldırıldı: her gezinmede WebSocket kopup
              yeniden bağlanıyor ve 4 HTTP isteği tekrar atılıyordu. */}
      {/* ========================================= */}
      {/* SAĞ SÜTUN: GÜNLÜK YARIŞ TABLOSU */}
      {/* ========================================= */}
      <div className="flex-1 relative z-10 overflow-y-auto pb-10 lg:pb-0">
        <div className="max-w-5xl mx-auto">

          {/* Üst Kısım: Başlık ve Sayaç */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-12 bg-[#1A1D20] border border-amber-500/20 rounded-3xl p-8 shadow-[0_0_50px_rgba(245,158,11,0.1)]">
            <div className="flex items-center gap-6">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-amber-400 to-amber-600 rounded-2xl flex items-center justify-center shadow-lg shadow-amber-500/20 transform -rotate-6 shrink-0">
                <Trophy className="w-8 h-8 md:w-10 md:h-10 text-white" />
              </div>
              <div>
                <h1 className="text-3xl md:text-5xl font-heading font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-500 uppercase tracking-widest drop-shadow-sm mb-2">
                  Daily Race
                </h1>
                <p className="text-zinc-400 font-body flex items-center gap-2 text-sm md:text-base">
                  <Swords className="w-4 h-4 text-amber-500 shrink-0" />
                  Play games, climb the leaderboard, win huge prizes!
                </p>
              </div>
            </div>

            <div className="bg-[#15171a] border border-[#2A2D31] rounded-2xl p-4 md:p-5 text-center min-w-[180px] md:min-w-[200px] shrink-0">
              <p className="text-xs text-zinc-500 uppercase tracking-widest font-bold mb-2 flex items-center justify-center gap-1">
                <Timer className="w-4 h-4 text-[#B059E8]" /> Ends In
              </p>
              <p className="text-2xl md:text-3xl mm-num font-bold text-white tracking-wider">
                {formatTime(timeLeft)}
              </p>
            </div>
          </div>

          {/* ========================================= */}
          {/* KÜRSÜ: TOP 3 (orta-üst / sol-orta / sağ-alt) */}
          {/* ========================================= */}
          {(first || second || third) && (
            <div className="flex items-end justify-center gap-2 sm:gap-3 md:gap-5 mb-12 px-1">
              <PodiumCard player={second} place={2} />
              <PodiumCard player={first} place={1} />
              <PodiumCard player={third} place={3} />
            </div>
          )}

          {/* ========================================= */}
          {/* GERİ KALAN SIRALAMA (4+) */}
          {/* ========================================= */}
          {rest.length > 0 && (
            <>
              {/* Tablo Başlıkları */}
              <div className="grid grid-cols-12 gap-4 px-4 md:px-6 mb-3 text-[10px] md:text-xs font-bold text-zinc-500 uppercase tracking-widest">
                <div className="col-span-2 md:col-span-1 text-center">Rank</div>
                <div className="col-span-6 md:col-span-5">Player</div>
                <div className="col-span-4 md:col-span-3 text-right">Wager</div>
                <div className="hidden md:block col-span-3 text-right">Prize</div>
              </div>

              <div className="space-y-3 mb-8">
                {rest.map((player) => (
                  <motion.div
                    key={player.user_id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: player.rank * 0.04 }}
                    className="grid grid-cols-12 gap-2 md:gap-4 items-center p-3 md:p-4 rounded-2xl border bg-[#1A1D20] border-[#2A2D31] transition-all hover:scale-[1.01] hover:border-[#3a3d43]"
                  >
                    <div className="col-span-2 md:col-span-1 font-heading font-black text-xl md:text-2xl text-center text-zinc-500">
                      #{player.rank}
                    </div>

                    <div className="col-span-6 md:col-span-5 flex items-center gap-2 md:gap-3">
                      <PlayerAvatar
                        username={player.username}
                        hasMc={player.roblox_username}
                        sizeClass="w-8 h-8 md:w-10 md:h-10"
                      />
                      <span className="font-bold text-white text-sm md:text-base truncate">{player.username}</span>
                    </div>

                    <div className="col-span-4 md:col-span-3 text-right flex flex-col items-end justify-center gap-0.5">
                      <span className="mm-num font-bold text-[#2ecf6e] text-sm md:text-base flex items-center gap-1">
                        {formatCompact(player.wager)}<RobuxIcon size={13} className="ml-1" />
                      </span>
                      {/* Mobilde ödül (masaüstünde ayrı sütunda) */}
                      <span className="md:hidden mm-num font-bold text-amber-300 text-[11px]">
                        {formatCompact(player.prize)}<RobuxIcon size={13} className="ml-1" />
                      </span>
                    </div>

                    <div className="hidden md:flex col-span-3 justify-end items-center gap-2">
                      <Coins className="w-4 h-4 text-zinc-400" />
                      <span className="mm-num font-bold text-white">
                        {formatCompact(player.prize)}<RobuxIcon size={13} className="ml-1" />
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </>
          )}

          {/* ========================================= */}
          {/* KULLANICININ KENDİ DURUMU (SADECE GİRİŞ YAPANLARDA) */}
          {/* ========================================= */}
          {raceData?.current_user && (
            <>
              <div className="flex items-center gap-4 mb-4">
                <div className="h-px bg-[#2A2D31] flex-1"></div>
                <span className="text-[10px] md:text-xs font-bold text-zinc-500 uppercase tracking-widest">Your Position</span>
                <div className="h-px bg-[#2A2D31] flex-1"></div>
              </div>

              <div className="grid grid-cols-12 gap-2 md:gap-4 items-center p-3 md:p-4 rounded-2xl border-2 border-[#B059E8]/50 bg-[#B059E8]/5 shadow-[0_0_30px_rgba(176,89,232,0.1)]">
                <div className="col-span-2 md:col-span-1 font-heading font-black text-lg md:text-xl text-center text-[#c98bf0]">
                  {raceData.current_user.rank === 0 ? "-" : `#${raceData.current_user.rank}`}
                </div>

                <div className="col-span-6 md:col-span-5 flex items-center gap-2 md:gap-3">
                  <PlayerAvatar
                    username={raceData.current_user.username}
                    hasMc={raceData.current_user.roblox_username}
                    sizeClass="w-8 h-8 md:w-10 md:h-10"
                    ringClass="border-[#B059E8]/50 border-2"
                  />
                  <div className="min-w-0">
                    <span className="font-bold text-white text-sm md:text-base block truncate">{raceData.current_user.username} (You)</span>
                  </div>
                </div>

                <div className="col-span-4 md:col-span-3 text-right flex flex-col items-end justify-center gap-0.5">
                  <span className="mm-num font-bold text-[#2ecf6e] text-sm md:text-base">
                    {formatCompact(raceData.current_user.wager)}<RobuxIcon size={13} className="ml-1" />
                  </span>
                  <span className="md:hidden mm-num font-bold text-amber-300 text-[11px]">
                    {raceData.current_user.prize > 0 ? `${formatCompact(raceData.current_user.prize)}$` : '0 $'}
                  </span>
                </div>

                <div className="hidden md:flex col-span-3 justify-end items-center gap-2">
                  <span className="mm-num font-bold text-zinc-400">
                    {raceData.current_user.prize > 0 ? `${formatCompact(raceData.current_user.prize)}$` : '0 $'}
                  </span>
                </div>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
};